import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/ui/SelectField';
import { Spinner } from '../../components/ui/Spinner';
import { BOT_LEAD_SOURCES, type BotLeadSource } from '../../types/leadBot';
import { createLead, listBotConfigs } from './api';

// MOCK LEAD INTAKE. Phase 1 wires the website form and Meta lead forms; this is
// the hand-entered stand-in that lets the whole loop be exercised now, and it
// captures deliberately the same shape those integrations will supply.
//
// ⚠️ The account list is the set of accounts with an ACTIVE BOT, not the whole
// roster: adding a lead for a client with no bot would create a conversation
// nothing can answer, and "there is no bot for this client" is better said here
// than discovered as an empty queue.

const SOURCE_LABELS: Record<BotLeadSource, string> = {
  website: 'Website enquiry',
  meta_lead_form: 'Meta lead form',
  phone: 'Phone enquiry',
  walk_in: 'Walk-in',
  other: 'Other',
};

export function NewLeadDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (conversationId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [accountId, setAccountId] = useState('');
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [source, setSource] = useState<BotLeadSource>('meta_lead_form');
  const [campaign, setCampaign] = useState('');

  const { data: configs = [] } = useQuery({
    queryKey: ['lead-bot', 'configs'],
    queryFn: listBotConfigs,
    enabled: open,
  });

  const accountOptions = configs
    .filter((c) => c.isActive)
    .map((c) => ({
      value: c.accountId,
      label: c.accountName ?? c.accountId,
      description: c.name,
    }));

  const reset = () => {
    setAccountId('');
    setLeadName('');
    setLeadPhone('');
    setLeadEmail('');
    setSource('meta_lead_form');
    setCampaign('');
  };

  const create = useMutation({
    mutationFn: () =>
      createLead({
        accountId,
        leadName: leadName.trim(),
        leadPhone: leadPhone.trim(),
        leadEmail: leadEmail.trim() === '' ? null : leadEmail.trim(),
        source,
        campaign: campaign.trim() === '' ? null : campaign.trim(),
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['lead-bot'] });
      // ⚠️ A skipped draft is REPORTED rather than silently absent — otherwise
      // an empty conversation looks like the feature is broken when the real
      // cause is a missing API key or a cap.
      if (result.draftSkipped) {
        toast.warning(`Lead added, but no reply was drafted: ${result.draftSkipped.detail}`, {
          duration: 10_000,
        });
      } else if (result.sent) {
        toast.success('Lead added — the opening message has been sent.');
      } else if (result.sendBlocked) {
        // ⚠️ THE TRAP THIS EXISTS TO CATCH: adding a test lead on a number that
        // is not on the bot's allowlist. The draft is created and simply never
        // goes out, which looks like the feature being broken unless it says so.
        toast.warning(`Lead added, but the opening message did not send: ${result.sendBlocked}`, {
          duration: 12_000,
        });
      } else {
        toast.success('Lead added — the opening message is waiting for approval.');
      }
      reset();
      onOpenChange(false);
      onCreated(result.conversation.id);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const canSubmit = accountId !== '' && leadName.trim() !== '' && leadPhone.trim() !== '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a lead</DialogTitle>
          {/* ⚠️ Deliberately does NOT say "nothing is sent until you approve it".
              That was true in draft mode and is not true of an autonomous bot,
              and a dialog that promises a review step which does not exist is
              worse than one that says nothing. */}
          <DialogDescription>
            Starts a conversation and writes the opening message. Whether it sends straight away
            or waits for approval depends on the client's bot.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="lead-account">Client</Label>
            <Combobox
              value={accountId}
              onValueChange={setAccountId}
              options={accountOptions}
              placeholder={
                accountOptions.length === 0 ? 'No clients have an active bot yet' : 'Select a client'
              }
              searchPlaceholder="Search clients…"
              emptyText="No client with an active bot matches."
              id="lead-account"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lead-name">Name</Label>
              <Input
                id="lead-name"
                value={leadName}
                onChange={(e) => setLeadName(e.target.value)}
                placeholder="Jane Smith"
              />
            </div>
            <div>
              <Label htmlFor="lead-phone">Mobile</Label>
              <Input
                id="lead-phone"
                value={leadPhone}
                onChange={(e) => setLeadPhone(e.target.value)}
                placeholder="0400 000 000"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="lead-email">Email (optional)</Label>
            <Input
              id="lead-email"
              type="email"
              value={leadEmail}
              onChange={(e) => setLeadEmail(e.target.value)}
              placeholder="jane@example.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lead-source">Source</Label>
              <SelectField
                id="lead-source"
                value={source}
                onValueChange={(v) => setSource(v as BotLeadSource)}
                options={BOT_LEAD_SOURCES.map((s) => ({ value: s, label: SOURCE_LABELS[s] }))}
              />
            </div>
            <div>
              <Label htmlFor="lead-campaign">Campaign (optional)</Label>
              <Input
                id="lead-campaign"
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
                placeholder="August motorhome offer"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={!canSubmit || create.isPending}>
            {create.isPending ? <Spinner className="h-4 w-4" /> : null}
            Add lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
